import { useState } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import { visuallyHidden } from '@mui/utils';
import { ClearAll } from '@mui/icons-material';
import {
  FilterList,
  InfoOutlined,
  Search,
  CloudDownload,
} from '@mui/icons-material';
import { Badge, IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  CheckboxFilter,
  TableCheckboxFilter,
} from './filters/TableCheckboxFilter';
import { TableTextFilter, TextFilter } from './filters/TableTextFilter';
import {
  isAppliedTextFilter,
  isAppliedCheckboxFilter,
  isAppliedFilters,
} from '../../utils/table.util';
import { HeadCell, useIndexTableContext } from './index_table.context';
import * as XLSX from 'xlsx';
import moment from 'moment';

interface IndexTableHeadProps {
  // checkboxes?: boolean;
  // expandOnCheckbox?: boolean;
  // iconHead?: any;
  // headCells: HeadCell[];
  numSelected: number;
  // defaultCheckboxFilters: CheckboxFilter;
  // checkboxFilters: CheckboxFilter;
  // textFilters: TextFilter;
  // onCheckboxFilterItem: (
  //   filterName: string,
  //   key: string,
  //   value: boolean
  // ) => void;
  // onCheckboxFilterSelectAll: (filterName: string) => void;
  // onCheckboxFilterUnselectAll: (filterName: string) => void;
  // onChangeTextFilter: (filterName: string, value: string) => void;
  // onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  // onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // order: 'asc' | 'desc';
  // orderBy: string;
  rowCount: number;
  // hideclearFilters?: boolean;
  // clearFilters: () => void;
  // exportData?: () => void;
  // selectMultiple?: boolean;
}

export const IndexTableHead = ({
  rowCount,
  numSelected,
}: IndexTableHeadProps) => {
  const {
    checkboxes = false,
    expandOnCheckbox = false,
    iconHead,
    headCells,
    additionalHeadCells,
    order,
    orderBy,
    selected,
    defaultCheckboxFilters,
    checkboxFilters,
    textFilters,
    updateCheckboxFilters: onCheckboxFilterItem,
    selectAllCheckboxFilters: onCheckboxFilterSelectAll,
    unselectAllCheckboxFilters: onCheckboxFilterUnselectAll,
    updateTextFilters: onChangeTextFilter,
    handleSelectAllClick: onSelectAllClick,
    handleRequestSort: onRequestSort,
    hideclearFilters,
    clearFilters,
    exportFormat,
    filteredData,
    title,
    selectMultiple,
  } = useIndexTableContext();

  const exportData = exportFormat
    ? () => {
        if (exportFormat) {
          const [formattedRows] = exportFormat(filteredData);
          downloadToXlsx(formattedRows, `${title}`);
        }
      }
    : undefined;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [anchorId, setAnchorId] = useState<string>('');
  const [anchorFilterType, setAnchorFilterType] = useState<string>();
  const [anchorLabel, setAnchorLabel] = useState<string>('');

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    headCell: HeadCell
  ) => {
    setAnchorId(headCell.textFilter || headCell.name);
    setAnchorEl(event.currentTarget);
    setAnchorFilterType(
      headCell.checkboxFilter
        ? 'checkbox'
        : headCell.textFilter
        ? 'text'
        : undefined
    );
    setAnchorLabel(headCell.label);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <>
      <TableHead
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.22)',
          border: '1px solid rgba(0, 0, 0, 0.19)',
        }}
      >
        <TableRow>
          {(checkboxes || expandOnCheckbox) && (
            <TableCell padding="checkbox">
              {iconHead
                ? iconHead
                : !!selectMultiple && (
                    <Checkbox
                      color="primary"
                      indeterminate={numSelected > 0 && numSelected < rowCount}
                      checked={rowCount > 0 && numSelected === rowCount}
                      onChange={onSelectAllClick}
                      // inputProps={{
                      //   'aria-label': 'select all desserts',
                      // }}
                    />
                  )}
            </TableCell>
          )}
          {headCells.map((headCell, index) =>
            headCell.clearFilter ? (
              <TableCell
                padding={!!headCell.disablePadding ? 'none' : 'normal'}
                align={headCell.align || 'left'}
                key={`head-clear-filter`}
                sx={{ fontWeight: 800, ...headCell.sx }}
                colSpan={headCell.colSpan}
                rowSpan={headCell.rowSpan}
              >
                {exportData && (
                  <Tooltip title="Export data">
                    <IconButton
                      onClick={() => {
                        exportData();
                      }}
                      className="ml-2"
                      size="large"
                    >
                      <CloudDownload />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Clear all filters">
                  <IconButton
                    aria-label="filter list"
                    onClick={() => clearFilters()}
                    size="large"
                  >
                    <Badge
                      color="secondary"
                      variant="dot"
                      invisible={
                        !isAppliedFilters(
                          defaultCheckboxFilters,
                          checkboxFilters,
                          textFilters
                        )
                      }
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <ClearAll />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </TableCell>
            ) : (
              <TableCell
                key={`${headCell.name}-${index}`}
                colSpan={headCell.colSpan}
                rowSpan={headCell.rowSpan}
                align={headCell.align || 'left'}
                padding={!!headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.name ? order : false}
                sx={
                  orderBy === headCell.name
                    ? { bgcolor: 'rgba(118, 176, 65, 0.08)', ...headCell.sx }
                    : { ...headCell.sx }
                }
              >
                <TableSortLabel
                  disabled={!headCell.sort}
                  active={orderBy === headCell?.sort}
                  direction={orderBy === headCell?.sort ? order : 'asc'}
                  onClick={createSortHandler(headCell?.sort || '')}
                  hideSortIcon={!headCell.sort}
                >
                  {headCell.label}
                  {orderBy === headCell?.sort ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>

                {headCell.checkboxFilter && (
                  <IconButton
                    component="span"
                    size="small"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      handleClick(event, headCell)
                    }
                  >
                    <Badge
                      color="secondary"
                      variant="dot"
                      invisible={
                        !isAppliedCheckboxFilter(
                          defaultCheckboxFilters,
                          checkboxFilters,
                          headCell.name
                        )
                      }
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <FilterList fontSize="inherit" />
                    </Badge>
                  </IconButton>
                )}
                {headCell.textFilter && (
                  <IconButton
                    component="span"
                    size="small"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      handleClick(event, headCell)
                    }
                  >
                    <Badge
                      color="secondary"
                      variant="dot"
                      invisible={
                        !isAppliedTextFilter(textFilters, headCell.name)
                      }
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <Search fontSize="inherit" />
                    </Badge>
                  </IconButton>
                )}
              </TableCell>
            )
          )}
        </TableRow>
        {additionalHeadCells && (
          <TableRow>
            {additionalHeadCells.map((headCell, index) => (
              <TableCell
                key={`${headCell.name}-${index}`}
                colSpan={headCell.colSpan}
                rowSpan={headCell.rowSpan}
                align={headCell.align || 'left'}
                padding={!!headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.name ? order : false}
                sx={
                  orderBy === headCell.name
                    ? { bgcolor: 'rgba(118, 176, 65, 0.08)', ...headCell.sx }
                    : { ...headCell.sx }
                }
              >
                <TableSortLabel
                  disabled={!headCell.sort}
                  active={orderBy === headCell?.sort}
                  direction={orderBy === headCell?.sort ? order : 'asc'}
                  onClick={createSortHandler(headCell?.sort || '')}
                  hideSortIcon={!headCell.sort}
                >
                  {headCell.label}
                  {orderBy === headCell?.sort ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>

                {headCell.checkboxFilter && (
                  <IconButton
                    component="span"
                    size="small"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      handleClick(event, headCell)
                    }
                  >
                    <Badge
                      color="secondary"
                      variant="dot"
                      invisible={
                        !isAppliedCheckboxFilter(
                          defaultCheckboxFilters,
                          checkboxFilters,
                          headCell.name
                        )
                      }
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <FilterList fontSize="inherit" />
                    </Badge>
                  </IconButton>
                )}
                {headCell.textFilter && (
                  <IconButton
                    component="span"
                    size="small"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      handleClick(event, headCell)
                    }
                  >
                    <Badge
                      color="secondary"
                      variant="dot"
                      invisible={
                        !isAppliedTextFilter(textFilters, headCell.name)
                      }
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <Search fontSize="inherit" />
                    </Badge>
                  </IconButton>
                )}
              </TableCell>
            ))}
          </TableRow>
        )}
      </TableHead>
      {anchorFilterType === 'checkbox' && (
        <TableCheckboxFilter
          anchorEl={anchorEl}
          anchorId={anchorId}
          checkboxFilters={checkboxFilters}
          handleClose={handleClose}
          onItem={onCheckboxFilterItem}
          onSelectAll={onCheckboxFilterSelectAll}
          onUnselectAll={onCheckboxFilterUnselectAll}
        />
      )}

      {anchorFilterType === 'text' && (
        <TableTextFilter
          anchorEl={anchorEl}
          anchorId={anchorId}
          anchorLabel={anchorLabel}
          textFilters={textFilters}
          handleClose={handleClose}
          updateText={onChangeTextFilter}
        />
      )}
    </>
  );
};

const downloadToXlsx = (data: any, title: string) => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Convert JSON data to a worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  // Convert workbook to a binary string
  const workbookBinary = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'binary',
  });

  // Create a Blob object from the binary string
  const blob = new Blob([s2ab(workbookBinary)], {
    type: 'application/octet-stream',
  });

  // Generate a download link
  const downloadLink = document.createElement('a');
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.download = `${title}-${moment().format('MM-DD-YYYY')}.xlsx`;

  // Trigger the download
  downloadLink.click();
};

// Helper function to convert string to ArrayBuffer
function s2ab(s: any) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < s.length; i++) {
    view[i] = s.charCodeAt(i) & 0xff;
  }
  return buf;
}
